import { getCSRFToken } from "#src/js/utils.mts"
import { extractFormData } from "#src/vue/forms/response.mts"
import { debounce, defaultsDeep, } from 'lodash'
import { cloneDeep, sortBy, uniqueId } from 'lodash/fp'
import type { DebuggerEvent, MaybeRef, MaybeRefOrGetter, Ref } from 'vue'
import { computed, inject, onMounted, provide, reactive, ref, toRaw, toRef, toRefs, toValue, unref, watch } from "vue"
import { RouteLocationRaw, useRoute, useRouter } from "vue-router"


// https://github.com/django/django/blob/main/django/forms/formsets.py
export const DELETION_FIELD_NAME = 'DELETE'
export const ORDERING_FIELD_NAME = 'ORDER'

export type MaybeList<T, Multiple extends boolean> = (Multiple extends true ? (Multiple extends false ? (T[] | T) : T[]) : ((Multiple extends false ? T : never)))
export type FieldValue<M extends boolean> = MaybeList<string, M> | MaybeList<number, M> | MaybeList<boolean, M> | null

export type FieldSubWidget = {
  // The name of this widget, if different from the parent field
  name?: string
  value: FieldValue<false>
  // the label to show when representing this widget normally, and when searching.
  label: string
  // the label to use when representing this widget as a tag.
  tag_label?: string
  attrs?: Record<string, string>
  is_hidden?: boolean
  required?: boolean
  type?: string
}

export type WidgetOption = any

export type Widget<M extends boolean> = {
  input_type?: string
  allow_multiple_selected?: M,
  is_hidden: boolean,
  is_required: boolean,
  taggable?: boolean,
  type?: string,

  label?: string,

  use_css_class?: boolean
  selected_option_attrs?: Record<string, string>
  limit?: number

  component?: string
  attrs?: Record<string, any>,
  optgroups?: [string, any[]],
}

export type RawField = {
  max_length?: number
  min_length?: number
}

/**
 * Tags are options that are created locally.
 */
export type Tag = {
  // The value of a tag. Should be created using lodash/uniqueId('tag-')
  value: string
  // The label of a tag. Taken from user input. Care must be taken to escape html before creating the tag.
  label: string
}

export type BoundFormField<Multiple extends boolean> = {
  name: string
  label: string
  value: FieldValue<Multiple>
  /**
   * { [value: string] => label }
   * user created options. See DjangoSelectWidget and vue-multiselect "taggable".
   * Care must be taken to escape user input before storing it as the tag label
   * */
  tags?: Record<string, string>,
  help_text: string
  errors: string[]
  feedback?: string[]
  is_hidden: boolean
  widget_type: string
  // attributes from bound_field.field.widget.attrs
  widget: Widget<Multiple>,
  option_component?: string
  placeholder?: string
  subwidgets: FieldSubWidget[]

  // local data
  id_for_label: string
  html_name: string // this is set to a ref, but when read in component templates the type is not unwrapped, so we type it as a string.
  _order?: Record<string, number> // this is used by fields that have internal order. Not by formsets.
  _is_mounted: boolean
}

export type ResultGroup = {
    limit: string
    css_class: string
    label: string
}

export type RankedChoiceBoundFormField = BoundFormField<true> & {
    value: string[]
    widget: BoundFormField<true>['widget'] & {
        result_groups: ResultGroup[]
        limit: number
        class: string
    }
    subwidgets: FieldSubWidget[]
}

export type DjangoFormAttrs = {
  modalClasses?: string[]
}

export type DjangoFormData = {
  title: string
  attrs?: DjangoFormAttrs,
  action: string | null
  method: string | null
  prefix: string | null
  is_bound: boolean
  empty_permitted: boolean
  label_suffix: string | null
  use_required_attribute: boolean
  submit_on_change?: boolean

  field_order: string[] | null
  fields: Record<string, BoundFormField<boolean>>
  non_field_errors: string[]
  non_field_feedback: string[]

  /** congress defined props */
  // Skip client side validation
  novalidate?: boolean
  confirm?: string | null

  // Local state

  // The form is pending if it is being submitted.
  pending: MaybeRef<boolean>
  // store a computed value somewhere so that each formset form can use the same one.
  __pendingRef?: Ref<boolean>
  // a unique id for use as key in vue forloops (and the id of the form if you're into that.)
  id: string
}

export type FormSetData = {
  absolute_max: number
  can_delete: boolean
  can_delete_extra: boolean
  can_order: boolean
  // edit_only doesn't seem to exist
  edit_only: boolean
  extra: number
  max_num: number
  min_num: number
  prefix: string
  validate_max: boolean
  validate_min: boolean
  empty_form: DjangoFormData,
  empty_form_template?: string,
  readonly?: boolean
  unique_fields?: string[]
  management_form: {
    TOTAL_FORMS: number
    INITIAL_FORMS: number
    MAX_NUM_FORMS: number
    MIN_NUM_FORMS: number
  }
  non_field_errors: string[],
  forms: DjangoFormData[]
}

// Used when mapping options manually. See DjangoCssClassSelector for an example.
export type FieldOption = ({ label: string, value: FieldValue<false>, } & Partial<FieldSubWidget>)

export type AsyncData = {
  form: DjangoFormData
  inlines: FormSetData[]
  layout?: string
  // is set to false when parse is complete
  pending?: boolean
} | {
  form: undefined
  inlines: undefined
  layout: undefined
  pending: boolean
}

export type ResponseData = {
  actions: any[]
  response: Response | null
  source: MaybeRef<HTMLElement>
}

const debugTrigger = (event: DebuggerEvent) => console.debug('computed debug trigger', event.key, event.type, event.target, event)

type t = DjangoFormData

export function useFormDataJson(input: Ref<string | null | undefined>) {
  const data = ref<AsyncData>({form: undefined, inlines: undefined, layout: undefined, pending: false}) as Ref<AsyncData>
  const nullData = { form: undefined, inlines: undefined, layout: undefined, pending: false, }

  watch(input, (_string) => {
    try {
      const rawData = JSON.parse(_string ?? 'null')
      if (rawData === null) {
        data.value = nullData
        return
      }
      // store the first element of the array result.formData as formData
      const { formData: [_formData] = [] } = extractFormData(rawData)
      const { form } = _formData ?? {}
      if (!form) {
        data.value = nullData
        return
      }
      rawData.pending = true
      data.value = reactive(_formData) as AsyncData
      console.debug("Updated form data from useFormData watcher")
    } catch (error) {
      data.value = nullData
    }
  }, { flush: 'pre', immediate: true, onTrigger: (event: DebuggerEvent) => console.debug('computed debug trigger', event.key, event.type, event.target) })

  return useFormData(data)
}

export function useFormData(rawData: MaybeRef<AsyncData> | MaybeRef<AsyncData | null> | MaybeRef<AsyncData | undefined>) {

  let unwatch: (() => void) | null = null

  if (rawData) {
    // this watcher will proc when entirely new data is registered.
    // watch is only deep when a reactive object, not a reference, is used.
    // (unless option deep: true is set.)
    watch(rawData, () => {
      // explicitly stop old watchers.
      // probably don't need to do that, but it doesn't hurt
      unwatch?.()
      unwatch = null

      const data = unref(rawData)
      const { form, inlines, layout, } = data ?? {}
      console.debug("useFormData watcher", unref(rawData))
      if (!form) {
        return
      }

      once(form, () => _rootFormInit(form))
      const unwatchArr = inlines?.map((formset) => {
        once(formset, () => _formsetInit(form, formset))
        return _watchFormsetData(formset)
      }) ?? []

      const _data = unref(data)
      if (_data) {
        _data.pending = false
      }
      unwatch = () => unwatchArr.forEach(f => f())
    }, { immediate: true, flush: 'pre',onTrigger: (event: DebuggerEvent) => console.debug('computed debug trigger', event.key, event.type, event.target) })
  }

  return {
    form: computed(() => unref(rawData)?.form, { onTrigger: debugTrigger }),
    inlines: computed(() => unref(rawData)?.inlines ?? [], { onTrigger: debugTrigger }),
    layout: computed(() => unref(rawData)?.layout, { onTrigger: debugTrigger }),
    pending: computed(() => unref(rawData)?.pending, { onTrigger: debugTrigger }),
    data: rawData,
  } as const
}

export function provideRootFormData(props: AsyncData) {
  const { form, inlines,  } = toRefs(props)
  const data = { form, inlines, }
  provide('django-root-form-data', data)
  return data
}
export function injectRootFormData() {
  return inject('django-root-form-data') as ReturnType<typeof provideRootFormData>
}

export function provideFormSetData(props: { formset: FormSetData }) {
  const { formset } = toRefs(props)
  const data = { formset, }
  provide('django-formset-data', data)
  return data
}

export function injectFormSetData() {
  return inject('django-formset-data') as ReturnType<typeof provideFormSetData>
}

export function provideFormFieldData(props: { form: DjangoFormData, field: BoundFormField<boolean>, }) {
  const { form, field, } = toRefs(props)
  const data = { form, field, }
  provide('django-form-field-data', data)
  return data
}

export function injectFormFieldData() {
  return inject('django-form-field-data') as ReturnType<typeof provideFormFieldData>
}

export function parseFormData ({ form, inlines, }: AsyncData, data: FormData) {

    // handle csrf tokens when they are set on the page level and not in form
    const csrf = data.get('csrfmiddlewaretoken')
    if (!csrf) {
        const token = getCSRFToken()
        if (!token) {
            console.warn("Could not determine csrf token value")
            return
        }
        data.set('csrfmiddlewaretoken', token)
    }

    const forms = [
        form,
        ...(inlines ?? []).map(({ forms }) => forms).flat(),
    ];

    forms.forEach((form) => {
        const { fields, } = form ?? {}
        Object.values(fields ?? {}).forEach((field: BoundFormField<boolean>) => {
            const isFileUpload = field.widget.input_type === 'file'

            const shouldIgnore_upload = ([isFileUpload].some(b => b)) as boolean
            if (shouldIgnore_upload) {
                return
            }

            const shouldIgnore_unhandled = !field._is_mounted
            if (shouldIgnore_unhandled) {
              return
            }

            // NB! html_name is a computed ref. We are forced to type it as a string due to linting errors
            const name = unref(field.html_name)
            const value = field.value

            if (name === undefined) {
                console.debug("Undefined name during FormData listener", name, value, field, form)
            }

            // override any data that rendered valid inputs by removing the data first.
            if (data.has(name)) {
              console.debug("onFormData: Deleting existing value", name, data)
              data.delete(name)
            }

            console.debug("appending value from field", name, value)
            if (Array.isArray(value)) {
                value.forEach(_value => {
                  data.append(name, String(_value))
                })
            } else if (value) {
                data.append(name, String(value))
            }
        })
    })
    return data
}

/**
 *
 * initializers
 */

function _rootFormInit (form: DjangoFormData) {
  /**
   * populate client side fields
   *  * form.pending
   */
  if (!form) {
    return
  }

  form.pending = false;
  // this reference is used for sub forms, so that we only need one computed ref for the entire form + inlines
  form.__pendingRef = computed(() => form.pending as boolean, { onTrigger: debugTrigger })

  _formInit(form)
}

function _formInit(form: DjangoFormData) {

  if (!form.id) {
    form.id = form.prefix
      ? uniqueId(`form-${form.prefix}-`)
      : uniqueId('form-')
  }

  Object.values(form.fields ?? {}).forEach((field) => {
    // issues with Ref type not being unwrapped in templates => type the fields as strings and hope we don't use them in scripts.
    // @ts-ignore
    field.id_for_label = computed(() => form.prefix ? `id_${form.prefix}-${field.name}` : `id_${field.name}`, { onTrigger: debugTrigger })
    // @ts-ignore
    field.html_name = computed(() => form.prefix ? `${form.prefix}-${field.name}` : `${field.name}`, { onTrigger: debugTrigger })
    field._is_mounted = false

    // Make sure field value is only an array if it should be.
    if (field.widget.allow_multiple_selected && !Array.isArray(field.value)) {
      field.value = ((field.value === undefined || field.value === null) ? [] : [field.value]) as string[]
    }

    if (!field.widget.allow_multiple_selected && Array.isArray(field.value)) {
      field.value = field.value[0]
    }
  })

  console.debug("_formInit", form.id, form)
}


function _formsetInit (rootForm: DjangoFormData, formset: FormSetData) {

  console.debug("Formset init", formset, formset.forms.map(form => Object.entries(form.fields).map(([_, field]) => [form.title, field.name, field.value])).flat())
  const { empty_form } = formset

  empty_form.is_bound = false
  empty_form.pending = rootForm.__pendingRef!

  const field_order = empty_form.fields[ORDERING_FIELD_NAME]

  if (formset.can_order) {
    if (field_order) {
      field_order.value = Number.MAX_SAFE_INTEGER
    } else {
      console.debug("Sanity check failed: formset.can_order with missing order field ")
    }
  }
}

function _formsetFormInit (formset: FormSetData, form: DjangoFormData, index: number) {
  const { empty_form: empty_form_raw } = formset
  console.debug("empty form prefix", empty_form_raw.prefix)

  const empty_form = cloneDeep(toRaw(empty_form_raw))

  form.id = uniqueId(`formset-${formset.prefix}-`)
  defaultsDeep(form, toRaw(empty_form))
  form.prefix = form.prefix?.replace("__prefix__", String(index)) ?? null

  _formInit(form)
}

/**
 * Whenever a new form is added: initialize the new form.
 * Whenever a form is marked for deletion and is local: delete the form.
 *
 * Whenever formset form ordering changes, either from addition, deletion, or setting the _order field,
 * re-order the forms.
 *
 * Finally, update the management form to reflect the current state.
 * @param formset the formset to watch
 */
function _watchFormsetData (formset: FormSetData) {
  return watch(
    [
      // trigger on a form being added, prefix changed, or deleted or undeleted
      () => formset.forms.map(({ prefix, fields, }) => `${prefix}-${fields?.[DELETION_FIELD_NAME]?.value ? 'active' : 'deleted'}`),
    ],
    () => {
      /**
       * This watcher is quite eager.
       * Therefore, we carefully check if an update is needed before assignment.
       *
       * Otherwise we get loops.
      */

      // initialize any new forms
      formset.forms.forEach((form, index) => once(
        form,
        () => _formsetFormInit(formset, form, index)
      ))

      // this callback will tend to change ordering and prefixes, so will generally run twice if it runs once.
      _updateManagementFormDataDebounce(formset)
    },
    { immediate: true, flush: 'pre', onTrigger: debugTrigger}
  )
}


const _updateManagementFormDataDebounce = debounce(
  _updateManagementFormData,
  100,
  { leading: true, trailing: true, },
)
/**
 * Watcher action.
 * @param formset The formset to update
 */
function _updateManagementFormData (formset: FormSetData) {
  console.count("UpdateManagementFormData")

  // re-order formset forms to put deleted ones last
  const _forms = sortBy<DjangoFormData>(
    [
      ({fields}: DjangoFormData) => fields?.[DELETION_FIELD_NAME]?.value ? 1 : 0,
      ({fields}: DjangoFormData) => fields?.[ORDERING_FIELD_NAME]?.value ?? Number.MAX_SAFE_INTEGER,
      ({prefix}: DjangoFormData) => prefix,
    ],
  )(formset.forms ?? [])

  // delete local forms
  const formDeletionData = formset.forms
    .map((item, index) => ({
      id: item.id,
      fieldId: unref(item.fields?.[DELETION_FIELD_NAME]?.id_for_label),
      index,
      shouldDelete: !item.is_bound && item.fields[DELETION_FIELD_NAME]?.value
    }))

  const deletedLocalForms = formDeletionData
    .filter(({shouldDelete}) => shouldDelete)
    .reverse()

  deletedLocalForms.forEach(({index, id, ...data}) => {
    const form = formset.forms[index]
    console.debug("Deleting local form", form.fields?.[DELETION_FIELD_NAME]?.value, index, id, formset.forms[index].id, data)
    formset.forms.splice(index, 1)
  })

  // re order forms so we don't skip prefixes
  if (formset.can_order && (ORDERING_FIELD_NAME in formset.empty_form.fields)) {
    let shouldUpdateFormOrder = false
    _forms.forEach((form, index) => {
      const orderField = form?.fields?.[ORDERING_FIELD_NAME]
      if (orderField === undefined) {
        return
      }
      if (orderField.value !== index) {
        console.debug("updating ordering field on formset form", orderField.value, index)
        orderField.value = index
        shouldUpdateFormOrder = true
        form.prefix = formset.empty_form.prefix?.replace('__prefix__', String(index)) ?? null
      }
    })

    if (shouldUpdateFormOrder) {
      formset.forms = _forms
    }
  }

  const activeForms = activeFormsetForms(formset)
  const total_forms = activeForms.length
  formset.management_form.TOTAL_FORMS = total_forms

  console.debug("Formset updated", formset)
}

/**
 * We want to register that a form field as been mounted, and only update the formdata based on that field if
 * it changes.
 */
export function useFieldMount<F extends BoundFormField<boolean>>(props: { field: F }) {
  onMounted(() => {
    watch(toRef(props.field), () => {
      props.field._is_mounted = true
    }, { immediate: true, onTrigger: debugTrigger})
  })
}

export function activeFormsetForms(formset: FormSetData, active: boolean = true) {
  const { forms } = formset

  const activeForms = forms.slice().filter(
    (form: DjangoFormData) => (Boolean(form.fields?.[DELETION_FIELD_NAME]?.value) !== active)
  )

  return activeForms
}

export function useActiveFormsetForms(formset: MaybeRef<FormSetData>) {
  const activeForms = computed(() => activeFormsetForms(unref(formset)), { onTrigger: debugTrigger })
  const deletedForms = computed(() => activeFormsetForms(unref(formset), false), { onTrigger: debugTrigger } )

  return { activeForms, deletedForms, }
}

/**
 * helpers
 */

const _onceState = new WeakSet()
function once(item: any, callback: () => void) {
  const _raw = toRaw(item)
  if (_onceState.has(_raw)) {
    return false
  }
  _onceState.add(_raw)
  callback()
  return true
}
